import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { useDispatch } from 'react-redux';
import { Link as RouterLink } from 'react-router-dom';
import {
  Box,
  Typography,
  TextField,
  CircularProgress,
  InputAdornment,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogContentText,
  DialogActions,
  Button,
  IconButton,
  Autocomplete,
  Chip,
  Link,
} from '@mui/material';
import SearchIcon from '@mui/icons-material/Search';
import AddIcon from '@mui/icons-material/Add';
import EditOutlinedIcon from '@mui/icons-material/EditOutlined';
import DeleteOutlineIcon from '@mui/icons-material/DeleteOutline';
import { AppDispatch } from '../../store/store';
import { getClinicGroupsApi } from '../../rtk-apis/clinics/getClinicGroups';
import { saveClinicGroupApi } from '../../rtk-apis/clinics/saveClinicGroup';
import { deleteClinicGroupApi } from '../../rtk-apis/clinics/deleteClinicGroup';
import { getFeatureControlApi } from '../../rtk-apis/clinics/getFeatureControl';
import { cardiacTokens as T } from '../../theme/cardiacTheme';

/* Clinic groups: a name, its clinics and its Group Admin users (users with role
   "Group Admin", added in Manage Users). A user belongs to at most one group. */

interface Option {
  _id: string;
  name: string;
  email?: string;
}

interface Group {
  _id: string;
  name: string;
  clinicIds: (Option | null)[];
  userIds: (Option | null)[];
  updatedAt?: string;
}

interface Form {
  id?: string;
  name: string;
  clinics: Option[];
  users: Option[];
}

const EMPTY_FORM: Form = { name: '', clinics: [], users: [] };

const cardSx = {
  background: `linear-gradient(180deg, rgba(20,36,57,0.62), rgba(14,28,46,0.55))`,
  border: `1px solid ${T.line}`,
  borderRadius: '16px',
  p: '18px 20px',
  minWidth: 0,
};

const fieldSx = {
  '& .MuiOutlinedInput-root': { color: T.ink },
  '& .MuiInputLabel-root': { color: T.ink3 },
  '& .MuiOutlinedInput-notchedOutline': { borderColor: T.line },
  '& .MuiChip-root': { color: T.ink, background: T.surface2 },
};

const GRID_COLUMNS = '1.3fr 2fr 2fr 90px';

const names = (list: (Option | null)[]) =>
  list.filter((o): o is Option => !!o).map(o => o.name).join(', ') || '—';

interface Props {
  notify?: (msg: string, sev: 'success' | 'error') => void;
}

const ClinicGroupsTable: React.FC<Props> = ({ notify }) => {
  const dispatch = useDispatch<AppDispatch>();
  // Held in a ref so `load` keeps its identity (the parent passes a new arrow each render).
  const notifyRef = useRef(notify);
  notifyRef.current = notify;
  const [groups, setGroups] = useState<Group[]>([]);
  const [groupAdmins, setGroupAdmins] = useState<Option[]>([]);
  const [clinics, setClinics] = useState<Option[]>([]);
  const [busy, setBusy] = useState(false);
  const [search, setSearch] = useState('');
  const [form, setForm] = useState<Form | null>(null);
  const [saving, setSaving] = useState(false);
  const [deleting, setDeleting] = useState<Group | null>(null);

  const load = useCallback(async () => {
    setBusy(true);
    try {
      const [groupsRes, clinicsRes]: any[] = await Promise.all([
        dispatch(getClinicGroupsApi()).unwrap(),
        dispatch(getFeatureControlApi()).unwrap(),
      ]);
      setGroups(Array.isArray(groupsRes?.data) ? groupsRes.data : []);
      setGroupAdmins(Array.isArray(groupsRes?.groupAdmins) ? groupsRes.groupAdmins : []);
      const list = clinicsRes?.result?.clinics;
      setClinics(Array.isArray(list) ? list.map((c: any) => ({ _id: c.clinicId, name: c.name })) : []);
    } catch (err) {
      console.error('[clinicGroups] fetch failed:', err);
      notifyRef.current?.('Could not load clinic groups.', 'error');
    } finally {
      setBusy(false);
    }
  }, [dispatch]);

  useEffect(() => {
    load();
  }, [load]);

  /** userId → name of the group that already holds it (other than the one being edited). */
  const takenBy = useMemo(() => {
    const out: Record<string, string> = {};
    groups.forEach(g => {
      if (g._id === form?.id) {
        return;
      }
      g.userIds.forEach(u => {
        if (u) {
          out[u._id] = g.name;
        }
      });
    });
    return out;
  }, [groups, form?.id]);

  const filtered = useMemo(() => {
    const q = search.trim().toLowerCase();
    if (!q) {
      return groups;
    }
    return groups.filter(
      g =>
        g.name.toLowerCase().includes(q) ||
        names(g.clinicIds).toLowerCase().includes(q) ||
        names(g.userIds).toLowerCase().includes(q)
    );
  }, [groups, search]);

  const openEdit = (g: Group) =>
    setForm({
      id: g._id,
      name: g.name,
      clinics: g.clinicIds.filter((o): o is Option => !!o),
      users: g.userIds.filter((o): o is Option => !!o),
    });

  const save = async () => {
    if (!form || !form.name.trim() || !form.clinics.length) {
      return;
    }
    setSaving(true);
    try {
      await dispatch(
        saveClinicGroupApi({
          id: form.id,
          name: form.name.trim(),
          clinicIds: form.clinics.map(c => c._id),
          userIds: form.users.map(u => u._id),
        })
      ).unwrap();
      notify?.(`${form.name.trim()} saved.`, 'success');
      setForm(null);
      load();
    } catch (err: any) {
      notify?.(typeof err === 'string' ? err : 'Could not save the group.', 'error');
    } finally {
      setSaving(false);
    }
  };

  const remove = async () => {
    if (!deleting) {
      return;
    }
    const group = deleting;
    setDeleting(null);
    try {
      await dispatch(deleteClinicGroupApi(group._id)).unwrap();
      notify?.(`${group.name} deleted.`, 'success');
      load();
    } catch (err: any) {
      notify?.(typeof err === 'string' ? err : 'Could not delete the group.', 'error');
    }
  };

  return (
    <Box>
      <Box sx={{ ...cardSx, p: '18px 20px 8px' }}>
        <Box
          sx={{
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'space-between',
            gap: 2,
            flexWrap: 'wrap',
            mb: '14px',
          }}
        >
          <Box>
            <Typography sx={{ fontSize: 14, fontWeight: 700, color: T.ink }}>
              Groups ({groups.length})
            </Typography>
            <Typography sx={{ fontSize: 12, color: T.ink3, mt: '2px' }}>
              {groupAdmins.length} Group Admin user{groupAdmins.length === 1 ? '' : 's'} ·{' '}
              <Link
                component={RouterLink}
                to="/adduser"
                sx={{ color: T.teal }}
              >
                add one in Manage Users
              </Link>{' '}
              with the role Group Admin
            </Typography>
          </Box>
          <Box sx={{ display: 'flex', gap: 1.5, alignItems: 'center' }}>
            <TextField
              size="small"
              placeholder="Search group, clinic or user"
              value={search}
              onChange={e => setSearch(e.target.value)}
              InputProps={{
                startAdornment: (
                  <InputAdornment position="start">
                    <SearchIcon sx={{ fontSize: 18, color: T.ink3 }} />
                  </InputAdornment>
                ),
              }}
              sx={{
                minWidth: 240,
                '& .MuiOutlinedInput-root': { color: T.ink, fontSize: 13 },
                '& .MuiOutlinedInput-notchedOutline': { borderColor: T.line },
              }}
            />
            <Button
              variant="contained"
              startIcon={<AddIcon />}
              onClick={() => setForm(EMPTY_FORM)}
              sx={{ background: T.teal2, textTransform: 'none', '&:hover': { background: T.teal2 } }}
            >
              Add group
            </Button>
          </Box>
        </Box>

        <Box sx={{ overflowX: 'auto' }}>
          <Box sx={{ minWidth: 800 }}>
            <Box
              sx={{
                display: 'grid',
                gridTemplateColumns: GRID_COLUMNS,
                gap: '12px',
                pb: '8px',
                borderBottom: `1px solid ${T.line}`,
                fontSize: 10.5,
                fontWeight: 700,
                letterSpacing: '0.08em',
                textTransform: 'uppercase',
                color: T.ink3,
              }}
            >
              <span>Group</span>
              <span>Clinics</span>
              <span>Group Admins</span>
              <span />
            </Box>

            {busy && !groups.length && (
              <Box sx={{ display: 'flex', justifyContent: 'center', py: '28px' }}>
                <CircularProgress size={22} sx={{ color: T.teal }} />
              </Box>
            )}

            {!busy && !filtered.length && (
              <Typography sx={{ fontSize: 13, color: T.ink3, py: '24px', textAlign: 'center' }}>
                {groups.length ? 'No group matches that search.' : 'No clinic groups yet.'}
              </Typography>
            )}

            {filtered.map(g => (
              <Box
                key={g._id}
                sx={{
                  display: 'grid',
                  gridTemplateColumns: GRID_COLUMNS,
                  gap: '12px',
                  alignItems: 'center',
                  py: '11px',
                  borderBottom: `1px solid ${T.line}`,
                  fontSize: 13.5,
                  color: T.ink,
                }}
              >
                <Typography sx={{ fontSize: 14, fontWeight: 600, color: '#fff' }}>{g.name}</Typography>
                <Typography sx={{ fontSize: 13, color: T.ink2 }}>{names(g.clinicIds)}</Typography>
                <Typography sx={{ fontSize: 13, color: T.ink2 }}>{names(g.userIds)}</Typography>
                <Box sx={{ display: 'flex', justifyContent: 'flex-end' }}>
                  <IconButton
                    size="small"
                    aria-label={`Edit ${g.name}`}
                    onClick={() => openEdit(g)}
                    sx={{ color: T.ink3 }}
                  >
                    <EditOutlinedIcon sx={{ fontSize: 18 }} />
                  </IconButton>
                  <IconButton
                    size="small"
                    aria-label={`Delete ${g.name}`}
                    onClick={() => setDeleting(g)}
                    sx={{ color: T.ink3 }}
                  >
                    <DeleteOutlineIcon sx={{ fontSize: 18 }} />
                  </IconButton>
                </Box>
              </Box>
            ))}
          </Box>
        </Box>
      </Box>

      <Dialog
        open={!!form}
        onClose={() => setForm(null)}
        PaperProps={{ sx: { background: T.surface, border: `1px solid ${T.line}`, width: 520 } }}
      >
        <DialogTitle sx={{ color: '#fff', fontSize: 16, fontWeight: 700 }}>
          {form?.id ? `Edit ${form.name}` : 'Add clinic group'}
        </DialogTitle>
        <DialogContent>
          <TextField
            fullWidth
            size="small"
            label="Group name"
            value={form?.name || ''}
            onChange={e => setForm(f => (f ? { ...f, name: e.target.value } : f))}
            sx={{ mt: '6px', mb: '16px', ...fieldSx }}
          />
          <Autocomplete
            multiple
            disableCloseOnSelect
            size="small"
            options={clinics}
            value={form?.clinics || []}
            onChange={(_e, clinics) => setForm(f => (f ? { ...f, clinics } : f))}
            getOptionLabel={o => o.name}
            isOptionEqualToValue={(a, b) => a._id === b._id}
            renderInput={params => (
              <TextField
                {...params}
                label="Clinics"
                sx={fieldSx}
              />
            )}
            sx={{ mb: '16px' }}
          />
          <Autocomplete
            multiple
            disableCloseOnSelect
            size="small"
            options={groupAdmins}
            value={form?.users || []}
            onChange={(_e, users) => setForm(f => (f ? { ...f, users } : f))}
            getOptionLabel={o => o.name}
            getOptionDisabled={o => !!takenBy[o._id]}
            noOptionsText="No Group Admin users yet"
            isOptionEqualToValue={(a, b) => a._id === b._id}
            renderOption={(props, o) => (
              <li
                {...props}
                key={o._id}
              >
                <Box>
                  <Typography sx={{ fontSize: 13.5 }}>{o.name}</Typography>
                  <Typography sx={{ fontSize: 11.5, color: T.ink3 }}>
                    {takenBy[o._id] ? `In ${takenBy[o._id]}` : o.email}
                  </Typography>
                </Box>
              </li>
            )}
            renderTags={(value, getTagProps) =>
              value.map((o, i) => (
                <Chip
                  {...getTagProps({ index: i })}
                  key={o._id}
                  size="small"
                  label={o.name}
                />
              ))
            }
            renderInput={params => (
              <TextField
                {...params}
                label="Group Admin users"
                sx={fieldSx}
              />
            )}
          />
          <Typography sx={{ fontSize: 12, color: T.ink3, mt: '8px' }}>
            A Group Admin is a user with the role Group Admin.{' '}
            <Link
              component={RouterLink}
              to="/adduser"
              sx={{ color: T.teal }}
            >
              Add one in Manage Users
            </Link>
          </Typography>
        </DialogContent>
        <DialogActions>
          <Button
            onClick={() => setForm(null)}
            sx={{ color: T.ink2, textTransform: 'none' }}
          >
            Cancel
          </Button>
          <Button
            variant="contained"
            disabled={saving || !form?.name.trim() || !form?.clinics.length}
            onClick={save}
            sx={{ background: T.teal2, textTransform: 'none', '&:hover': { background: T.teal2 } }}
          >
            {saving ? 'Saving…' : 'Save'}
          </Button>
        </DialogActions>
      </Dialog>

      <Dialog
        open={!!deleting}
        onClose={() => setDeleting(null)}
        PaperProps={{ sx: { background: T.surface, border: `1px solid ${T.line}` } }}
      >
        <DialogTitle sx={{ color: '#fff', fontSize: 16, fontWeight: 700 }}>
          Delete {deleting?.name}?
        </DialogTitle>
        <DialogContent>
          <DialogContentText sx={{ color: T.ink2, fontSize: 13.5 }}>
            Its Group Admin users will no longer see these clinics. The users themselves are kept.
          </DialogContentText>
        </DialogContent>
        <DialogActions>
          <Button
            onClick={() => setDeleting(null)}
            sx={{ color: T.ink2, textTransform: 'none' }}
          >
            Cancel
          </Button>
          <Button
            variant="contained"
            onClick={remove}
            sx={{ background: T.red, textTransform: 'none', '&:hover': { background: T.red } }}
          >
            Delete
          </Button>
        </DialogActions>
      </Dialog>
    </Box>
  );
};

export default ClinicGroupsTable;
